import * as React from 'react'
import { Form, Input, Alert } from 'antd'
import { FormattedMessage, useIntl } from 'react-intl'
import InvoiceForm from './InvoiceForm'
import CompanyInfo from '../CompanyInfo'
import ItemsForm from './ItemsForm'
import SelectDiscount from './SelectDiscount'
import Amount from '../../Amount'
import { pipe } from 'fp-ts/lib/function'
import { array, number, option, record } from 'fp-ts'
import BillLayout from '../BillLayout'
import { FormInstance } from 'antd/lib/form'
import moment from 'moment'
import { useOrganization } from '@library/react-toolkit'
import SelectTax from './SelectTax'
import { Discount } from '../../Discounts'
import { Tax } from '../../TaxRates'
import styled from 'styled-components'
import { calculateRate } from '../../../types/Fees'
import SelectPayer from './SelectPayer'
import { useMode } from '../queries'
import {
  Customer,
  InvoiceItem,
  InvoiceWithTotal,
  NewDiscountOrTax,
} from '../../../types'
import OrganizationLogo from './OrganizationLogo'

export type FormData = {
  reference: string
  dueAt: moment.Moment
  unitID: string
  createdAt: moment.Moment
  payer?: Customer
  companyInfo?: {
    email: string
    name: string
  }
  discount?: Discount
  tax?: Tax
  description: string
  items: InvoiceItem[]
}

export type BillFormData = {
  reference: string
  dueAt: moment.Moment
  unitID: string
  createdAt: moment.Moment
  payer?: Customer
  companyInfo?: {
    email: string
    name: string
  }
  discount?: NewDiscountOrTax
  tax?: NewDiscountOrTax
  description: string
  items: InvoiceItem[]
}

export type BillFormDataWithAmount = BillFormData & { amount: number }

type Props = {
  title?: React.ReactNode
  form?: FormInstance
  onFinish?: (_: BillFormDataWithAmount) => void
  onChange?: (_: Partial<BillFormDataWithAmount>) => void
  initialValue?: InvoiceWithTotal
}

const TotalRow = styled.div`
  display: flex;
  align-items: center;
`

const TotalName = styled.div`
  padding-left: 8px;
  flex: 111;
`

const TotalAmount = styled.div`
  padding-left: 8px;
  flex: 212;
`

const itemsTotal = (unitID: string) =>
  array.foldMap(option.getMonoid(number.MonoidSum))((i: InvoiceItem) =>
    pipe(
      record.lookup(unitID, i.item.price),
      option.map(p => p * i.quantity)
    )
  )

const datesToMoments = ({ createdAt, dueAt, ...b }: InvoiceWithTotal) => ({
  ...b,
  createdAt: moment(createdAt),
  dueAt: moment(dueAt),
})

export default ({ title, form, onFinish, initialValue, onChange }: Props) => {
  const org = useOrganization()
  const mode = useMode()
  const [items, setItems] = React.useState<InvoiceItem[]>(
    initialValue?.items ?? []
  )
  const [unitID, setUnitID] = React.useState<string | undefined>(org?.asset)

  const [orgWithEmail, setOrgWithEmail] = React.useState<{
    organization?: string
    email?: string
  }>({
    organization: '',
    email: '',
  })

  const [discount, setDiscount] = React.useState<Discount>()
  const [tax, setTax] = React.useState<Tax>()

  const intl = useIntl()
  console.debug('initial values', initialValue)
  React.useEffect(() => {
    form?.resetFields()
  }, [initialValue?.id])
  const total = pipe(
    option.fromNullable(unitID),
    option.chain(u => pipe(items, itemsTotal(u))),
    option.getOrElse(() => 0)
  )
  const discountedAmount = !!discount
    ? total - calculateRate(discount.rate, total)
    : total
  const finalAmount = !!tax
    ? discountedAmount + calculateRate(tax.rate, total)
    : discountedAmount

  const itemsWithMissingPrice = pipe(
    items,
    array.filter(i =>
      pipe(
        option.fromNullable(unitID),
        option.chain(u => record.lookup(u, i.item.price)),
        option.isNone
      )
    ),
    array.map(i => i.item.name)
  )

  return (
    <Form<FormData>
      form={form}
      onValuesChange={(_, s) => {
        setItems(s.items ?? items)
        setUnitID(s.unitID ?? unitID)
        setDiscount(s.discount)
        setTax(s.tax)
        onChange?.({
          ...s,
          unitID,
          discount: s.discount,
          tax: s.tax,
          amount: total,
        })
      }}
      onFinish={b => {
        if (!!unitID) {
          onFinish?.({
            ...b,
            unitID,
            amount: total,
            discount: b.discount,
            tax: b.tax,
          })
        }
      }}
      initialValues={
        !!initialValue
          ? {
              ...datesToMoments(initialValue),
            }
          : { unitID: org?.asset }
      }
    >
      <BillLayout
        title={title}
        image={
          mode === 'organization' ? (
            <OrganizationLogo orgName={org?.name ?? ''} />
          ) : (
            <OrganizationLogo orgName={orgWithEmail.organization ?? ''} />
          )
        }
        invoiceInfo={
          <InvoiceForm
            org={orgWithEmail.organization}
            invalidUnit={itemsWithMissingPrice.length > 0}
          />
        }
        payer={
          <Form.Item
            name="payer"
            rules={[
              {
                required: mode === 'organization',
                message: <FormattedMessage id="required" />,
              },
            ]}
          >
            <SelectPayer placeholder={<FormattedMessage id="payer" />} />
          </Form.Item>
        }
        company={
          <Form.Item
            name="companyInfo"
            rules={[
              {
                required: mode === 'supplier',
                message: <FormattedMessage id="required" />,
              },
            ]}
          >
            <CompanyInfo
              value={orgWithEmail}
              onChange={v => {
                setOrgWithEmail(q => ({
                  ...q,
                  organization: v?.name,
                  email: v?.email,
                }))
              }}
            />
          </Form.Item>
        }
        items={
          <Form.Item name="items">
            <ItemsForm unitID={unitID} />
          </Form.Item>
        }
        discounts={
          <Form.Item name="discount">
            <SelectDiscount total={total} unit={unitID} />
          </Form.Item>
        }
        description={
          <Form.Item name="description">
            <Input.TextArea
              placeholder={intl.formatMessage({ id: 'termsAndConditions' })}
            />
          </Form.Item>
        }
        total={pipe(
          option.fromNullable(unitID),
          option.fold(
            () =>
              itemsWithMissingPrice.length > 0 ? (
                <Alert
                  message={
                    <FormattedMessage
                      id="missingCurrency"
                      values={{ items: itemsWithMissingPrice.join(', ') }}
                    />
                  }
                  type="error"
                />
              ) : (
                <Alert
                  message={<FormattedMessage id="invalidPrice" />}
                  type="error"
                />
              ),
            unitID => (
              <div>
                <TotalRow>
                  <TotalName>
                    <FormattedMessage id="subTotal" />
                  </TotalName>
                  <TotalAmount>
                    <Amount amount={discountedAmount} unit={unitID} />
                  </TotalAmount>
                </TotalRow>
                <Form.Item name="tax" style={{ marginBottom: 0 }}>
                  <SelectTax total={discountedAmount} unit={unitID} />
                </Form.Item>
                <TotalRow>
                  <TotalName>
                    <FormattedMessage id="total" />
                  </TotalName>
                  <TotalAmount>
                    <Amount amount={finalAmount} unit={unitID} />
                  </TotalAmount>
                </TotalRow>
              </div>
            )
          )
        )}
      />
    </Form>
  )
}
